/**
 * Built-in analytics backends. Each one is safe to register unconditionally — it
 * no-ops when its transport (GTM with consent, `window.posthog`) is absent, so
 * trackers can load late (or never) without breaking event dispatch.
 */
import { gtmTracking } from '@/utils/analytics/googleTagManager';
import type { AnalyticsEvent, AnalyticsProvider } from './types';

type DataLayerWindow = Window & { dataLayer?: Record<string, unknown>[] };
type PostHogWindow = Window & {
  posthog?: { capture?: (event: string, properties?: Record<string, unknown>) => void };
};

/**
 * Google Analytics via Google Tag Manager's `dataLayer`. GTM is loaded at runtime behind
 * marketing consent (`src/utils/analytics/googleTagManager.ts`), so this pushes only while
 * `gtmTracking()` holds: GTM replays queued events when it loads, and keeps evaluating them
 * after a withdrawal. Pushes a flat event GTM can route to a GA4 tag.
 */
export const gtmProvider: AnalyticsProvider = {
  name: 'gtm',
  track(event: AnalyticsEvent): void {
    if (!gtmTracking()) return;
    const w = window as DataLayerWindow;
    w.dataLayer = w.dataLayer ?? [];
    w.dataLayer.push({
      event: event.name,
      ga_id: event.gaId,
      ga_label: event.label,
      ga_derived: event.derived,
      ...event.props,
    });
  },
};

/**
 * PostHog — fires only when the SDK is present on `window`, so it can be registered
 * before (or without) PostHog being loaded. Part of the multi-tracker setup.
 */
export const posthogProvider: AnalyticsProvider = {
  name: 'posthog',
  track(event: AnalyticsEvent): void {
    const ph = (window as PostHogWindow).posthog;
    if (!ph?.capture) return;
    ph.capture(event.name, {
      ga_id: event.gaId,
      label: event.label,
      derived: event.derived,
      ...event.props,
    });
  },
};
